/**
 * Iter 73 Phase 7 — Utility Visualisation overlay.
 *
 * Reads project_objects with type=utility for the current sheet and
 * renders coloured dots (or color-coded crosshairs) at their normalized
 * locations. Honest scope: this is a MANUAL utility map. No fake
 * intelligence, no auto-detected conflicts. The colour comes from
 * `metadata.utility_kind`. Conflict status drives a halo / ring.
 *
 * Conflict halos:
 *   - none / verified_conflict-resolved: no halo
 *   - review_needed: amber dashed ring
 *   - potential_conflict: amber solid ring
 *   - verified_conflict: red ring
 *   - resolved: green ring (light)
 */
import React, { useEffect, useState } from "react";
import { listProjectObjects } from "@/lib/markupsApi";
import { UTILITY_COLORS } from "@/components/viewer/LayersPanel";

const CONFLICT_RING = {
  review_needed: { stroke: "#fbbf24", dash: "4,3", width: 2 },
  potential_conflict: { stroke: "#fbbf24", dash: "", width: 2.5 },
  verified_conflict: { stroke: "#ef4444", dash: "", width: 3 },
  resolved: { stroke: "#10b981", dash: "", width: 1.5 },
};

export default function UtilityOverlay({
  projectId, sheetId, visible = true, scale = 1, imgDims,
}) {
  const [rows, setRows] = useState([]);
  // B2-10 (independent recheck batch 2, 2026-10-01) — reopens R-06.
  // A denied/failed read used to silently resolve to `[]` here, which
  // `rows.length === 0` then rendered as "nothing to draw" — visually
  // IDENTICAL to a sheet that genuinely has no utilities marked. A
  // field user has no way to tell "verified: none" apart from
  // "unknown: couldn't check" just by looking at the drawing. Track
  // the read outcome separately so a failed/denied read is always
  // visible, and a transient failure on a background refresh never
  // wipes out utilities that were already correctly drawn.
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (!projectId || !sheetId) return;
    let alive = true;
    const load = () => listProjectObjects(projectId, { type: "utility", sheet_id: sheetId })
      .then((r) => {
        if (!alive) return;
        if (r?._forbidden) { setStatus("forbidden"); return; }
        if (r?._degraded) { setStatus("degraded"); return; }
        setRows(r || []);
        setStatus("ok");
      });
    load();
    return () => { alive = false; };
  }, [projectId, sheetId]);

  // Refresh when an object is created elsewhere in the viewer.
  useEffect(() => {
    const h = () => {
      if (!projectId || !sheetId) return;
      listProjectObjects(projectId, { type: "utility", sheet_id: sheetId })
        .then((r) => {
          if (r?._forbidden) { setStatus("forbidden"); return; }
          if (r?._degraded) { setStatus("degraded"); return; }
          setRows(r || []);
          setStatus("ok");
        });
    };
    window.addEventListener("forgedops:objects-updated", h);
    return () => window.removeEventListener("forgedops:objects-updated", h);
  }, [projectId, sheetId]);

  if (!visible || !imgDims) return null;
  if ((status === "forbidden" || status === "degraded") && rows.length === 0) {
    return (
      <div
        data-testid="utility-overlay-unavailable"
        style={{ position: "absolute", top: 8, left: 8, pointerEvents: "none" }}
        className="px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider bg-black/70 text-amber-300"
      >
        {status === "forbidden" ? "Utilities — access denied" : "Utilities — couldn't load"}
      </div>
    );
  }
  if (rows.length === 0) return null;

  const W = imgDims.width || 0;
  const H = imgDims.height || 0;
  if (W === 0 || H === 0) return null;

  return (
    <svg
      data-testid="utility-overlay"
      style={{
        position: "absolute", top: 0, left: 0,
        width: W * scale, height: H * scale,
        pointerEvents: "none",
      }}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
    >
      {rows.map((u) => {
        const loc = u.location;
        const geometry = u.metadata?.geometry;
        const kind = u.metadata?.utility_kind || "unknown";
        const fill = UTILITY_COLORS[kind] || UTILITY_COLORS.unknown;
        const ring = u.conflict_status && u.conflict_status !== "none"
          ? CONFLICT_RING[u.conflict_status] : null;
        // Iter 74 Phase 2 — polyline run rendering when metadata.geometry
        // is a `polyline` with ≥ 2 points. Falls back to point marker.
        const isRun = geometry?.type === "polyline" && Array.isArray(geometry.points) && geometry.points.length >= 2;
        if (isRun) {
          const pathD = geometry.points
            .map((p, i) => `${i === 0 ? "M" : "L"} ${p.nx * W} ${p.ny * H}`)
            .join(" ");
          // Label position = midpoint of the run.
          const mid = geometry.points[Math.floor(geometry.points.length / 2)];
          return (
            <g key={u.id} data-testid={`utility-run-${u.id}`} style={{ cursor: "pointer", pointerEvents: "auto" }}
               onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("forgedops:object-tap", { detail: { object_id: u.id } })); }}>
              {ring && (
                <path d={pathD} stroke={ring.stroke} strokeWidth={ring.width + 4}
                      strokeDasharray={ring.dash || undefined}
                      fill="none" opacity={0.6} />
              )}
              <path d={pathD} stroke={fill} strokeWidth={4} fill="none" strokeLinecap="round"
                    strokeLinejoin="round" opacity={0.95} />
              <path d={pathD} stroke="#0a0a0b" strokeWidth={6} fill="none" opacity={0.25}
                    style={{ paintOrder: "stroke" }} />
              <text x={mid.nx * W + 8} y={mid.ny * H + 4}
                    fontSize={12} fontFamily="monospace"
                    fill="#fff" stroke="#0a0a0b" strokeWidth={2}
                    paintOrder="stroke" style={{ userSelect: "none" }}>
                {u.label}
              </text>
            </g>
          );
        }
        if (!loc || typeof loc.nx !== "number" || typeof loc.ny !== "number") return null;
        const cx = loc.nx * W;
        const cy = loc.ny * H;
        const r = 8;
        return (
          <g key={u.id} data-testid={`utility-marker-${u.id}`}
             style={{ cursor: "pointer", pointerEvents: "auto" }}
             onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent("forgedops:object-tap", { detail: { object_id: u.id } })); }}>
            {ring && (
              <circle cx={cx} cy={cy} r={r + 6}
                      fill="none" stroke={ring.stroke}
                      strokeWidth={ring.width}
                      strokeDasharray={ring.dash || undefined}
                      opacity={0.9} />
            )}
            <circle cx={cx} cy={cy} r={r} fill={fill} stroke="#0a0a0b"
                    strokeWidth={1.5} opacity={0.9} />
            <text x={cx + r + 4} y={cy + 4}
                  fontSize={12} fontFamily="monospace"
                  fill="#fff" stroke="#0a0a0b" strokeWidth={2}
                  paintOrder="stroke" style={{ userSelect: "none" }}>
              {u.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
